import type { GraphEdge, GraphNode, OwnershipGraphData } from '../../../types'
import type { PositionedNode } from './layoutTree'
import {
  canExpandDown,
  canExpandDownRoot,
  canExpandUp,
} from './graphExpandEligibility'

/** Matches OwnershipGraph palette (tailwind editorial tokens). */
const C = {
  ink: '#1A1A1A',
  ink500: '#666664',
  ink400: '#8A8A86',
  ruleStrong: '#CFCBC2',
  paper: '#FAFAF7',
  paper200: '#F2F1EB',
  signal: '#B8451F',
  signal50: '#FBEDE6',
  signal900: '#5B2210',
} as const

const MAX_NODE_LABEL_CHARS = 21

export interface GraphSvgEdgeRow {
  d: string
  edge: GraphEdge
}

export interface BuildOwnershipGraphSvgInput {
  graphData: OwnershipGraphData
  positions: Record<string, PositionedNode>
  edgeRows: GraphSvgEdgeRow[]
  beneficialNodeIds: Set<string>
  svgWidth: number
  svgHeight: number
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function truncateLabel(navn: string): string {
  if (navn.length <= MAX_NODE_LABEL_CHARS) return navn
  return navn.slice(0, MAX_NODE_LABEL_CHARS - 1) + '…'
}

function expandChevronMarkup(cx: number, y: number, direction: 'up' | 'down'): string {
  const path = direction === 'down' ? 'M-5 -2 L0 4 L5 -2' : 'M-5 4 L0 -2 L5 4'
  return `<g transform="translate(${cx}, ${y})">
  <circle r="14" fill="${C.paper}" stroke="${C.ruleStrong}" stroke-width="1"/>
  <path d="${path}" fill="none" stroke="${C.ink500}" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
</g>`
}

function nodeMarkup(
  node: GraphNode,
  pos: PositionedNode,
  isBeneficialOwner: boolean,
): string {
  const { x, y, w, h } = pos
  const isRoot = !!node.isRoot
  const isForeign = node.kind === 'foreign-company'
  const isPerson = node.kind === 'person'
  const isTrunc = !!node.truncated
  const rx = isPerson ? h / 2 : 4

  let fillColor = C.paper
  let strokeColor = isForeign ? C.signal : isRoot ? C.ink : C.ruleStrong
  let strokeW = isRoot ? 2 : 1
  let textColor = isForeign ? C.signal900 : C.ink

  if (isTrunc) {
    fillColor = C.paper200
    strokeColor = C.ruleStrong
    textColor = C.ink400
  } else if (isBeneficialOwner) {
    fillColor = C.signal50
    strokeColor = C.signal
    strokeW = Math.max(strokeW, 1.75)
    textColor = C.signal900
  } else if (isForeign) {
    fillColor = C.signal50
  }

  const displayName = escapeXml(truncateLabel(node.navn || '—'))
  const hasCountry = node.countryCode && node.countryCode !== 'DK'
  const textY = hasCountry ? y + h / 2 - 5 : y + h / 2 + 4
  const fontFamily = isPerson
    ? "'Source Serif 4', 'Source Serif Pro', ui-serif, Georgia, serif"
    : "'Inter', ui-sans-serif, sans-serif"

  let out = `<g data-node-id="${escapeXml(node.id)}">
  <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeW}"/>
  <text x="${x + w / 2}" y="${textY}" text-anchor="middle" font-size="11" font-family="${fontFamily}" font-weight="${isRoot ? 600 : 400}" fill="${textColor}">${displayName}</text>`

  if (hasCountry) {
    out += `\n  <text x="${x + w / 2}" y="${y + h / 2 + 9}" text-anchor="middle" font-size="9" font-family="'Inter', ui-sans-serif, sans-serif" fill="${C.signal}">${escapeXml(node.countryCode!)}</text>`
  }

  if (isTrunc) {
    out += `\n  <text x="${x + w - 8}" y="${y + h / 2 + 4}" text-anchor="middle" font-size="9" fill="${C.ink400}" font-family="'Inter', ui-sans-serif, sans-serif">…</text>`
  }

  out += '\n</g>'
  return out
}

function collectExpandMarkers(
  graphData: OwnershipGraphData,
  positions: Record<string, PositionedNode>,
): string[] {
  const rootId = 'cvr:' + graphData.rootCvr
  const markers: string[] = []

  if (canExpandDownRoot(graphData)) {
    const rootPos = positions[rootId]
    if (rootPos) {
      markers.push(expandChevronMarkup(
        rootPos.x + rootPos.w / 2,
        rootPos.y + rootPos.h + 22,
        'down',
      ))
    }
  }

  for (const node of graphData.nodes) {
    const pos = positions[node.id]
    if (!pos) continue

    if (canExpandUp(node, rootId, graphData.edges)) {
      markers.push(expandChevronMarkup(pos.x + pos.w / 2, pos.y - 22, 'up'))
    }
    if (canExpandDown(node, rootId, graphData.edges)) {
      markers.push(expandChevronMarkup(pos.x + pos.w / 2, pos.y + pos.h + 22, 'down'))
    }
  }

  return markers
}

/** Build a standalone SVG string for the current graph state (full canvas). */
export function buildOwnershipGraphSvg(input: BuildOwnershipGraphSvgInput): string {
  const { graphData, positions, edgeRows, beneficialNodeIds, svgWidth, svgHeight } = input

  const edgeParts: string[] = []
  for (const row of edgeRows) {
    if (row.edge.kind === 'beneficial') continue
    edgeParts.push(
      `<path d="${row.d}" fill="none" stroke="${C.ruleStrong}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
    )
  }

  const nodeParts: string[] = []
  for (const node of graphData.nodes) {
    const pos = positions[node.id]
    if (!pos) continue
    nodeParts.push(nodeMarkup(node, pos, beneficialNodeIds.has(node.id)))
  }

  const expandParts = collectExpandMarkers(graphData, positions)

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgWidth} ${svgHeight}" width="${svgWidth}" height="${svgHeight}" role="img" aria-label="Ejerskabsgraf">
  <rect width="100%" height="100%" fill="${C.paper}"/>
  <g class="ownership-graph-edges">${edgeParts.join('\n  ')}</g>
  <g class="ownership-graph-nodes">${nodeParts.join('\n  ')}</g>
  <g class="ownership-graph-expand-markers">${expandParts.join('\n  ')}</g>
</svg>`
}

export function ownershipGraphSvgFilename(rootCvr: string): string {
  return `ejerskabsgraf-${rootCvr}.svg`
}

export function downloadOwnershipGraphSvg(svg: string, filename: string): void {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
